<script>
export default {
	name: 'OverlayBonusSideTrip',
	inheritAttrs: false,
};
</script>

<script setup>
import useDisableScroll from '~composables/useDisableScroll.js';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import HeaderBST from '~/components/BookingWidget/BonusSideTrip/HeaderBST.vue';
import FooterBST from '~/components/BookingWidget/BonusSideTrip/FooterBST.vue';
import EditSearchBST from '~/components/BookingWidget/BonusSideTrip/EditSearchBST.vue';
import OptionBST from '~/components/BookingWidget/BonusSideTrip/OptionBST.vue';
import FormWrapper from '~/components/form/FormWrapper.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import Breadcrumb from '~/components/Breadcrumb.vue';
import TextField from '~/components/form/TextField.vue';
import dayjs from 'dayjs';
import { useBSTOverlayInterimData } from '~composables/booking-widget/useBSTOverlayInterimData.js';
import { i18nGlobal } from '~/logic/i18n.js';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { getONDValueByAirportCode, getMalaysiaAirportForBST } from '~/logic/composables/booking-widget/useONDList.js';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import Tooltip from '~/components/Tooltip.vue';
import { sortBy as _sortBy } from 'lodash';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { getCookie } from '~/logic/helpers/cookies.js';
import { useIsUkVariant } from '~/logic/composables/booking-widget/usePassengers.js';
import DatePicker from '~/components/form/DatePicker.vue';
import AirportPicker from '~/components/BookingWidget/AirportPicker.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';

const props = defineProps({
	isVisible: { type: Boolean, default: false },
	bstNoteTop: { type: String, default: '' },
	bstNoteCTAText: { type: String, default: '' },
	bstNoteCTAURL: { type: String, default: '' },
	bstEditSearchCTAText: { type: String, default: '' },
	bstLabel: { type: String, default: '' },
	optionLabel: { type: String, default: '' },
	visitMalaysiaLabel: { type: String, default: '' },
	yourDateOfChoiceLabel: { type: String, default: '' },
	question1Text: { type: String, default: '' },
	question2Text: { type: String, default: '' },
	continueWithoutBSTCTAText: { type: String, default: '' },
	continueCTAText: { type: String, default: '' },
	toLabel: { type: String, default: '' },
	departLabel: { type: String, default: '' },
	bstQuestion1MobileLabel: { type: String, default: '' },
	bstQuestion2MobileLabel: { type: String, default: '' },
	bstDisabledContinueCtaLabel: { type: String, default: '' },
	ondSelection: { type: Object, default: null },
	datePicker: { type: Object, default: null },
	passengerSelection: { type: Object, default: null },
	fromQuestionMobile: { type: String, default: '' },
	toQuestionMobile: { type: String, default: '' },
	datePickerQuestionMobile: { type: String, default: '' },
	passengerSelectionQuestion: { type: String, default: '' },
	flightSearchApiUrl: { type: String, default: '' },
	cabinClassCashList: { type: Array, default: () => [] },
	cabinClassMilesList: { type: Array, default: () => [] },
	componentIdAA: { type: String, default: '' },
	extraONDFilterForFlightSearch: { type: Object, default: null },
	bstAssetType: { type: String, default: '' },
	bstAssetPath: { type: String, default: '' },
	reviewYourTripLabel: { type: String, default: '' },
	bonusSideTripLabel: { type: String, default: '' },
	freeJustPayTaxesLabel: { type: String, default: '' },
	visitAnotherCityText: { type: String, default: '' },
	enableBSTNewDesign: { type: Boolean, default: true },
	enableGlassDesign: { type: Boolean, default: true },
});

const emit = defineEmits([
	'update:isVisible',
]);

const rootEl = ref(null);
const option1El = ref(null);
const option2El = ref(null);
const selectedAirportOption1 = ref(null);
const selectedAirportOption2 = ref(null);
const selectedDateOption1 = ref(null);
const selectedDateOption2 = ref(null);
const internalVisible = ref(props.isVisible);

useDisableScroll(internalVisible, {
	useOverflowHiddenMethod: true,
});

watch(() => props.isVisible, (newValue) => {
	internalVisible.value = newValue;
});

watch(internalVisible, (newValue) => {
	emit('update:isVisible', newValue);
});

const hideOverlay = () => {
	internalVisible.value = false;
};

const { activate: activeFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(rootEl);

const handleRootElOnMounted = async () => {
	rootEl.value?.focus();
	await nextTick(); // oddly enough, we have to use nextTick in order for focus trap to work 🤔
	activeFocusTrap();
};

const handleRootElBeforeUnmount = () => {
	deactivateFocusTrap();
};
const editSearchBSTEl = ref(null);
const formWrapper = ref(null);
const { pageProperties, isEditorMode } = useGlobalAEMState();
const country = pageProperties.value.rootCountry;
const language = pageProperties.value.rootLanguage;

const capitalizeString = (string) => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

const handleSubmitValid = async (values, actions) => {
	console.log(' handleSubmitValid values = ', holdingData.value, values);
	const storedData = holdingData.value;

	/*
		The cabin class value from the passenger and cabin class dropdown is 1, 2, 3.
		Economy: 1,
		Business: 2,
		Business Suite: 3.
		While form submission, due to different form has different cabin class value, even though their label(Economy, Business, Business Suite) are the same. 
		require to map to the actual cabin class value, which come from the component config.
	*/
	const cabinClassIndex = parseInt(storedData.cabinClassValue.value) - 1;
	const cabinClassValue = storedData.payByEnrichPoints ? props.cabinClassMilesList[cabinClassIndex].value : props.cabinClassCashList[cabinClassIndex].value;
	let dataPayload = null;

	const sideTripAirportOpt1 = props.enableBSTNewDesign
		? selectedAirportOption1.value
		: option1El.value?.airportValue;

	const sideTripAirportOpt2 = props.enableBSTNewDesign
		? selectedAirportOption2.value
		: option2El.value?.airportValue;

	const sideTripDateOpt1 = props.enableBSTNewDesign
		? selectedDateOption1.value
		: option1El.value?.departureDate;

	const sideTripDateOpt2 = props.enableBSTNewDesign
		? selectedDateOption2.value
		: option2El.value?.departureDate;



	const isUKVariant = useIsUkVariant({ origin: storedData.ondValueFrom, destination: storedData.ondValueTo });

	if (values === 'bst') {
		switch (focusedOption.value) {
			case 'option-1':
				dataPayload = {
					'originCountry': storedData.ondValueFrom?._original?.countryName,
					'departDate1': dayjs(storedData.dateRangeValueFrom).format('YYYYMMDDHHmm'),
					'returnDate1': dayjs(storedData.dateRangeValueFrom).format('YYYYMMDDHHmm'),
					'originAirportCode1': storedData.ondValueFrom.code,
					'destAirportCode1': sideTripAirportOpt1?.code,

					'departDate2': dayjs(sideTripDateOpt1).format('YYYYMMDDHHmm'),
					'returnDate2': dayjs(sideTripDateOpt1).format('YYYYMMDDHHmm'),
					'originAirportCode2': sideTripAirportOpt1?.code,
					'destAirportCode2': storedData.ondValueTo.code,

					...(!isOneWay.value ? {
						'departDate3': dayjs(storedData.dateRangeValueTo).format('YYYYMMDDHHmm'),
						'returnDate3': dayjs(storedData.dateRangeValueTo).format('YYYYMMDDHHmm'),
						'originAirportCode3': storedData.ondValueTo.code,
						'destAirportCode3': storedData.ondValueFrom.code,
					} : null),

					'flightClass': cabinClassValue,
					'adultCount': storedData.adultsCount.toString(),
					...(isUKVariant ? { 'teenCount': storedData.teensCount.toString() } : null),
					'childCount': storedData.childrenCount.toString(),
					'infantCount': storedData.infantsCount.toString(),
					'paymentType': storedData.payByEnrichPoints ? 'miles' : 'cash',
					'regionLanguage': country && language ? `${country}-${capitalizeString(language)}` : '',
					'promoCode': storedData.promoCodeValue ?? '',
					'amcvId': window._satellite?.getVar('ECID') ?? '',
					'teaserCategory': getCookie('TeaserCategory') ?? '',
					'bstDestination': sideTripAirportOpt1?.code,
					'bstDepartureDate': sideTripDateOpt1
						? dayjs(sideTripDateOpt1).format('YYYYMMDDHHmm')
						: null,
					'bstBound': 'Departure',
				};
				break;

			case 'option-2':
				dataPayload = {
					'originCountry': storedData.ondValueFrom?._original?.countryName,
					'departDate1': dayjs(storedData.dateRangeValueFrom).format('YYYYMMDDHHmm'),
					'returnDate1': dayjs(storedData.dateRangeValueFrom).format('YYYYMMDDHHmm'),
					'originAirportCode1': storedData.ondValueFrom.code,
					'destAirportCode1': storedData.ondValueTo.code,

					'departDate2': dayjs(storedData.dateRangeValueTo).format('YYYYMMDDHHmm'),
					'returnDate2': dayjs(storedData.dateRangeValueTo).format('YYYYMMDDHHmm'),
					'originAirportCode2': storedData.ondValueTo?.code,
					'destAirportCode2': sideTripAirportOpt2?.code,

					'departDate3': dayjs(sideTripDateOpt2).format('YYYYMMDDHHmm'),
					'returnDate3': dayjs(sideTripDateOpt2).format('YYYYMMDDHHmm'),
					'originAirportCode3': sideTripAirportOpt2?.code,
					'destAirportCode3': storedData.ondValueFrom.code,

					'flightClass': cabinClassValue,
					'adultCount': storedData.adultsCount.toString(),
					...(isUKVariant ? { 'teenCount': storedData.teensCount.toString() } : null),
					'childCount': storedData.childrenCount.toString(),
					'infantCount': storedData.infantsCount.toString(),
					'paymentType': storedData.payByEnrichPoints ? 'miles' : 'cash',
					'regionLanguage': country && language ? `${country}-${capitalizeString(language)}` : '',
					'promoCode': storedData.promoCodeValue ?? '',
					'amcvId': window._satellite?.getVar('ECID') ?? '',
					'teaserCategory': getCookie('TeaserCategory') ?? '',
					'bstDestination': sideTripAirportOpt2?.code,
					'bstDepartureDate': sideTripDateOpt2
						? dayjs(sideTripDateOpt2).format('YYYYMMDDHHmm')
						: null,
					'bstBound': 'Return',
				};
				break;

			default:
				console.log(`Unknown value: ${focusedOption.value}. Ignore it.`);
				break;
		}

		dataPayload['bst'] = 'TRUE';

	} else {
		dataPayload = {
			'departDate1': dayjs(storedData.dateRangeValueFrom).format('YYYYMMDDHHmm'),
			'returnDate1': !storedData.isOneWay && storedData.dateRangeValueTo ? dayjs(storedData.dateRangeValueTo).format('YYYYMMDDHHmm') : '',
			'originAirportCode1': storedData.ondValueFrom.code,
			'originCountry': storedData.ondValueFrom._original.countryName,
			'destAirportCode1': storedData.ondValueTo.code,
			'flightClass': cabinClassValue,
			'adultCount': storedData.adultsCount.toString(),
			...(isUKVariant ? { 'teenCount': storedData.teensCount.toString() } : null),
			'childCount': storedData.childrenCount.toString(),
			'infantCount': storedData.infantsCount.toString(),
			'paymentType': storedData.payByEnrichPoints ? 'miles' : 'cash',
			'amal1': storedData.ondValueTo._original.isAmalFlag,
			'regionLanguage': country && language ? `${country}-${capitalizeString(language)}` : '',
			'promoCode': storedData.promoCodeValue ?? '',
			'amcvId': window._satellite?.getVar('ECID') ?? '',
			'teaserCategory': getCookie('TeaserCategory') ?? '',
		};
	}

	try {
		// 1st api to get response with url and payload for 2nd api
		const apiResponse = await axios({
			method: 'post',
			url: props.flightSearchApiUrl,
			data: dataPayload,
		});

		const { url, payload } = apiResponse.data;

		// 'RAW_DATA' doesn't required, delete it. 
		delete payload['RAW_DATA'];

		// 2nd api need to post with form submission
		// add extra property to payload object
		const generatedData = {};
		const entriesPayload = Object.entries(payload);
		for (let [index, [key, value]] of entriesPayload.entries()) {
			generatedData[index] = key;
		}
		const massageData = { ...generatedData, ...payload };
		const entriesMassageData = Object.entries(massageData);

		// create hidden form and submit
		const method = 'post';
		const form = document.createElement('form');
		form.setAttribute('method', method);
		form.setAttribute('action', url);

		for (let [index, [key, value]] of entriesMassageData.entries()) {
			// console.log(`apiResponse => ${index}: ${key}: ${value}`);
			const _key = parseInt(key);
			let hiddenField = document.createElement('input');
			hiddenField.setAttribute('type', 'hidden');
			hiddenField.setAttribute('name', key);
			hiddenField.setAttribute('value', value);
			form.appendChild(hiddenField);
		}
		document.body.appendChild(form);
		form.submit();

	} catch (e) {
		console.error('handleSubmitValid, ', 'Unable to fetch api', e);
	}

};

const handleSubmitInvalid = ({ values, errors, results, evt }) => {
	console.log('❌ handleSubmitInvalid errors = ', errors);
};

const handleBreadcrumbNavClick = (href, event) => {
	// Assume BST overlay only appears in homepage, home link is the only clickable link,
	// we can be sure that '@nav-link-click' only fires for "Home" then.
	// Edit: Actually, we have to hide the BST overlay on any breadcrumb link click, not just home.
	hideOverlay();
};

const focusedOption = ref(null); // option-1 | option-2 | null
const {
	holdingData,
	commitDataFromBSTOverlay,
	registerNotifier,
	unregisterNotifier,
} = useBSTOverlayInterimData();

const domesticAirportForBST = ref(null);

const getDomesticAirportForBST = async () => {
	if (!ondValueFrom?.value?.code && !ondValueTo?.value?.code) return;
	domesticAirportForBST.value = await getMalaysiaAirportForBST({
		from: ondValueFrom.value.code,
		to: ondValueTo.value.code,
	}) ?? null;

	// Sort it by A-Z

	if (domesticAirportForBST.value) {
		domesticAirportForBST.value.fromTo = _sortBy(
			domesticAirportForBST.value.fromTo,
			'label',
		);

		domesticAirportForBST.value.toFrom = _sortBy(
			domesticAirportForBST.value.toFrom,
			'label',
		);
	}

};

const ondValueFrom = ref(null);
const ondValueTo = ref(null);
const dateRangeValueFrom = ref(null);
const dateRangeValueTo = ref(null);
const isOneWay = ref(false);

/* This function is called when Subtab "Search Flight" CTA Button is clicked and form is submitted */
registerNotifier('bst-overlay', (newData) => {
	ondValueFrom.value = newData.ondValueFrom;
	ondValueTo.value = newData.ondValueTo;
	dateRangeValueFrom.value = newData.dateRangeValueFrom;
	dateRangeValueTo.value = newData.dateRangeValueTo;
	isOneWay.value = newData.isOneWay;

	getDomesticAirportForBST();
}, { immediate: true });

/* This function is called when "Save" CTA Button is clicked on <EditSearchBST> */
const handleEditSearchCommitData = (newData) => {
	// update local data
	ondValueFrom.value = newData.ondValueFrom;
	ondValueTo.value = newData.ondValueTo;
	dateRangeValueFrom.value = newData.dateRangeValueFrom;
	dateRangeValueTo.value = newData.dateRangeValueTo;
	isOneWay.value = newData.isOneWay;
	option1El.value?.resetForm();
	option2El.value?.resetForm();
	focusedOption.value = null;
	selectedAirportOption1.value = null;
	selectedAirportOption2.value = null;

	selectedDateOption1.value = null;
	selectedDateOption2.value = null;


	if (isOneWay.value) {
		focusedOption.value = 'option-1';
	}

	// date store
	commitDataFromBSTOverlay(newData);

	// get new domestic airport list with new ondValueFrom and new ondValueTo
	getDomesticAirportForBST();
};

const bstToMalaysiaValues = computed(() => {
	if (!formWrapper.value) return null;
	const formValues = formWrapper.value.values;

	let result = null;
	if (formValues.selectedOption === 'option-1') {
		result = {
			location: formValues['bst-option-1-locationInMalaysia'],
			departDate: formValues['bst-option-1-departDate'],
		};
	} else {
		result = {
			location: formValues['bst-option-2-locationInMalaysia'],
			departDate: formValues['bst-option-2-departDate'],
		};
	}

	return result;
});


const isBtnSubmitDisabled = computed(() => {

	if (!props.enableBSTNewDesign) {

		if (!formWrapper.value) return true;

		const formMeta = formWrapper.value.meta;

		if (editSearchBSTEl.value?.isEditMode) return true;

		return (
			!formMeta.valid ||
			!(
				bstToMalaysiaValues.value &&
				bstToMalaysiaValues.value.location &&
				bstToMalaysiaValues.value.departDate
			)
		);
	}

	if (!focusedOption.value) return true;

	if (focusedOption.value === 'option-1') {
		return !selectedAirportOption1.value || !selectedDateOption1.value;
	}

	if (focusedOption.value === 'option-2') {
		return !selectedAirportOption2.value || !selectedDateOption2.value;
	}

	return true;
});


const airportQuestionLabel = computed(() => {
	if (!currentContext.value) return '';

	if (isMobileViewport.value && props.bstQuestion1MobileLabel) {
		return props.bstQuestion1MobileLabel;
	}
	return currentContext.value.isReturn
		? props.question1TextReturn || props.question1Text
		: props.question1Text;
});

const airportInputLabel = computed(() => {
	return currentContext.value?.isReturn
		? props.toLabelReturn || props.toLabel
		: props.toLabel;
});

const dateQuestionLabel = computed(() => {
	if (!currentContext.value) return '';

	if (isMobileViewport.value && props.bstQuestion2MobileLabel) {
		return props.bstQuestion2MobileLabel;
	}

	return currentContext.value.isReturn
		? props.question2TextReturn || props.question2Text
		: props.question2Text;
});

const dateInputLabel = computed(() => {
	return currentContext.value?.isReturn
		? props.departLabelReturn || props.departLabel
		: props.departLabel;
});

const currentContext = computed(() => {

	if (!focusedOption.value) return null;

	const isReturn = focusedOption.value === 'option-2';

	return {
		isReturn,

		from: isReturn ? ondValueTo.value : ondValueFrom.value,
		to: isReturn ? ondValueFrom.value : ondValueTo.value,

		airportList: isReturn
			? domesticAirportForBST.value?.toFrom
			: domesticAirportForBST.value?.fromTo,

		minDate: isReturn
			? dateRangeValueTo.value
			: dateRangeValueFrom.value,

		maxDate: isReturn
			? null
			: dateRangeValueTo.value,

		displayDate: isReturn
			? dateRangeValueTo.value
			: dateRangeValueFrom.value,

		departDate: isReturn
			? dateRangeValueTo.value
			: dateRangeValueFrom.value,

		returnDate: dateRangeValueTo.value,
	};
});



const reviewTripFlow = computed(() => {
	if (!ondValueFrom.value || !ondValueTo.value || !focusedOption.value) return null;

	const isReturn = focusedOption.value === 'option-2';

	return {
		from: ondValueFrom.value,
		to: ondValueTo.value,
		departDate: dateRangeValueFrom.value,
		returnDate: isOneWay.value ? null : dateRangeValueTo.value,
		bstAirport: selectedAirport.value,
		bstDate: selectedDate.value,
		isReturn,
	};
});


const mobileSteps = computed(() => {
	if (!reviewTripFlow.value) return [];
	const bstTrip = reviewTripFlow.value;
	if (!bstTrip.isReturn) {
		return [
			{
				type: 'location',
				label: `${bstTrip.from.displayValue} ${bstTrip.from.code}`,
			},
			{
				type: 'date',
				label: dayjs(bstTrip.departDate).format('DD MMM YYYY'),
			},
			{
				type: 'bst',
				label: bstTrip.bstAirport
					? `${bstTrip.bstAirport.label} ${bstTrip.bstAirport.code}`
					: props.visitMalaysiaLabel,
			},
			{
				type: 'date',
				label: bstTrip.bstDate
					? dayjs(bstTrip.bstDate).format('DD MMM YYYY')
					: props.yourDateOfChoiceLabel,
			},
			{
				type: 'location',
				label: `${bstTrip.to.displayValue} ${bstTrip.to.code}`,
			},
			...(bstTrip.returnDate
				? [
					{
						type: 'date',
						label: dayjs(bstTrip.returnDate).format('DD MMM YYYY'),
					},
					{
						type: 'location',
						label: `${bstTrip.from.displayValue} ${bstTrip.from.code}`,
					},
				]
				: []),
		];
	}

	return [
		{
			type: 'location',
			label: `${bstTrip.from.displayValue} ${bstTrip.from.code}`,
		},
		{
			type: 'date',
			label: dayjs(bstTrip.departDate).format('DD MMM YYYY'),
		},
		{
			type: 'location',
			label: `${bstTrip.to.displayValue} ${bstTrip.to.code}`,
		},
		{
			type: 'date',
			label: dayjs(bstTrip.returnDate).format('DD MMM YYYY'),
		},
		{
			type: 'bst',
			label: bstTrip.bstAirport
				? `${bstTrip.bstAirport.label} ${bstTrip.bstAirport.code}`
				: props.visitMalaysiaLabel,
		},
		{
			type: 'date',
			label: bstTrip.bstDate
				? dayjs(bstTrip.bstDate).format('DD MMM YYYY')
				: props.yourDateOfChoiceLabel,
		},
		{
			type: 'location',
			label: `${bstTrip.from.displayValue} ${bstTrip.from.code}`,
		},
	];
});

const selectedAirport = computed({
	get () {
		return focusedOption.value === 'option-2'
			? selectedAirportOption2.value
			: selectedAirportOption1.value;
	},
	set (val) {
		if (focusedOption.value === 'option-2') {
			selectedAirportOption2.value = val;
		} else {
			selectedAirportOption1.value = val;
		}
	},
});

const selectedDate = computed({
	get () {
		return focusedOption.value === 'option-2'
			? selectedDateOption2.value
			: selectedDateOption1.value;
	},
	set (val) {
		if (focusedOption.value === 'option-2') {
			selectedDateOption2.value = val;
		} else {
			selectedDateOption1.value = val;
		}
	},
});

const bstImageSrc = computed(() => {
	return isMobileViewport.value && props.bstMobileImage
		? props.bstMobileImage
		: props.bstDesktopImage;
});

const bstImageAlt = computed(() => {
	return isMobileViewport.value
		? props.bstMobileImageAltText
		: props.bstDesktopImageAltText;
});

onUnmounted(() => {
	unregisterNotifier('bst-overlay');
});

</script>

<template>
<Teleport to="body">
	<Transition name="overlay-scale-fade-in" appear>
		<div
			v-if="isVisible"
			ref="rootEl"
			tabindex="-1"
			class="OverlayBonusSideTrip generic-backdrop"
			v-bind="$attrs"
			@keydown.esc.capture="hideOverlay"
			@vue:mounted="handleRootElOnMounted"
			@vue:beforeUnmount="handleRootElBeforeUnmount"
			@click.self="handleBackdropClick"
		>
			<FormWrapper
				ref="formWrapper"
				v-slot="{ values, errors, meta }"
				class="inner-container"
				tabindex="-1"
				@submit-valid="handleSubmitValid('bst')"
				@submit-invalid="handleSubmitInvalid"
			>
				<div
					class="overlay-content overflow-y-auto styled-scrollbar h-full h-[100svh] flex flex-col"
					v-bind="props.overlayContentAttrs"
				>
					<header class="bst-header">
						<HeaderBST class="overlay-header"></HeaderBST>
						<div class="bg-neutral-grey-ultralight py-4">
							<div class="generic-container">
								<Breadcrumb :isForBonusSideTrip="true" @nav-link-click="handleBreadcrumbNavClick" />
							</div>
						</div>
					</header>

					<div class="bst-body generic-container flex-grow">
						<!-- <p>values: <code>{{ values }}</code></p> -->
						<!-- <div class="" style="height: 700px; flex-basis: 700px;"></div> -->
						<div class="max-w-[996px] mx-auto lg:w-full py-10">
							<template v-if="!props.enableBSTNewDesign">
								<div v-if="props.bstNoteTop" class="bst-banner rounded-xl bg-primary-blue-extralight flex-grow px-5 py-4 flex gap-4 relative sm:flex-col">
									<div class="flex gap-3">
										<div class="basis-6 flex-none top-1 relative">
											<icon-fas-circle-info class="fill-primary-blue-base" aria-hidden="true" />
										</div>
										<div v-html-sanitize="props.bstNoteTop"></div>
									</div>
								
									<div v-if="props.bstNoteCTAText" class="static sm:pl-8 rtl:right-auto rtl:left-6 rtl:sm:pl-0 rtl:sm:pr-8 flex-shrink-0">
										<AppHyperlink
											v-aa="[
												{
													clickName: props.bstNoteCTAText,
													clickComponentType: 'URL',
													componentName: 'OverlayBonusSideTrip',
													componentID: props.componentIdAA,
													BST: true,
												},
												{
													name: props.bstNoteCTAText,
													type: isUrlMatchCurrentHostname(props.bstNoteCTAURL) ? 'other' : 'exit',
												},
											]"
											:href="props.bstNoteCTAURL"
											target="_blank"
											:showExternalLinkIcon="false"
										>
											{{ props.bstNoteCTAText }}
										</AppHyperlink>
									</div>
								</div>
							
								<div class="bst-edit-search mt-7 mb-7">
									<EditSearchBST
										ref="editSearchBSTEl"
										:bstEditSearchCTAText="props.bstEditSearchCTAText"
										:ondSelection="props.ondSelection"
										:datePicker="props.datePicker"
										:passengerSelection="props.passengerSelection"
										:fromQuestionMobile="props.fromQuestionMobile"
										:toQuestionMobile="props.toQuestionMobile"
										:datePickerQuestionMobile="props.datePickerQuestionMobile"
										:passengerSelectionQuestion="props.passengerSelectionQuestion"
										:componentIdAA="props.componentIdAA"
										:extraONDFilterForFlightSearch="props.extraONDFilterForFlightSearch"
										:enableGlassDesign="props.enableGlassDesign"
										@save-search-data="handleEditSearchCommitData"
									/>
								</div>
								<div v-html-sanitize="props.bstLabel" class="text-lg font-semibold mb-7"></div>
								<div class="flex flex-col gap-6 mb-10">
									<OptionBST
										ref="option1El"
										v-aa="[
											{
												clickName: `${props.optionLabel} 1`,
												clickComponentType: 'Button',
												componentName: 'OverlayBonusSideTrip',
												componentID: props.componentIdAA,
												BST: true,
											},
											{
												name: `${props.optionLabel} 1`,
												type: 'other',
											},
										]"
										class=""
										formNamePrefix="bst-option-1"
										:locationA="ondValueFrom"
										:locationB="ondValueTo"
										:domesticAirportList="domesticAirportForBST?.fromTo"
										:dateToMalaysia="dateRangeValueFrom"
										:dateLeaveMalaysia="dateRangeValueTo"
										:isFocused="focusedOption === 'option-1'"
										:isOneWay="isOneWay"
										:visitMalaysiaLabel="props.visitMalaysiaLabel"
										:yourDateOfChoiceLabel="props.yourDateOfChoiceLabel"
										:question1Text="props.question1Text"
										:question2Text="props.question2Text"
										:toLabel="props.toLabel"
										:departLabel="props.departLabel"
										:enableBSTNewDesign="props.enableBSTNewDesign"
										:enableGlassDesign="props.enableGlassDesign"
										@focus-within="focusedOption = 'option-1'"
									>
										<template #title>
											{{ `${$t('Departure')}: ${ondValueFrom.displayValue} (${ondValueFrom.code}) ${$t('to')} ${ondValueTo.displayValue} (${ondValueTo.code})` }}
										</template>
										<template #mobile-question-1-title>
											{{ props.bstQuestion1MobileLabel }}
										</template>
										<template #mobile-question-2-title>
											{{ props.bstQuestion2MobileLabel }}
										</template>
									</OptionBST>
									<OptionBST
										v-if="!isOneWay"
										ref="option2El"
										v-aa="[
											{
												clickName: `${props.optionLabel} 2`,
												clickComponentType: 'Button',
												componentName: 'OverlayBonusSideTrip',
												componentID: props.componentIdAA,
												BST: true,
											},
											{
												name: `${props.optionLabel} 2`,
												type: 'other',
											},
										]"
										formNamePrefix="bst-option-2"
										:locationA="ondValueTo"
										:locationB="ondValueFrom"
										:domesticAirportList="domesticAirportForBST?.toFrom"
										:dateToMalaysia="dateRangeValueTo"
										:isFocused="focusedOption === 'option-2'"
										:isOneWay="isOneWay"
										:visitMalaysiaLabel="props.visitMalaysiaLabel"
										:yourDateOfChoiceLabel="props.yourDateOfChoiceLabel"
										:question1Text="props.question1Text"
										:question2Text="props.question2Text"
										:toLabel="props.toLabel"
										:departLabel="props.departLabel"
										:enableBSTNewDesign="props.enableBSTNewDesign"
										:enableGlassDesign="props.enableGlassDesign"
										@focus-within="focusedOption = 'option-2'"
									>
										<template #title>
											{{ `${$t('Return')}: ${ondValueTo.displayValue} (${ondValueTo.code}) ${$t('to')} ${ondValueFrom.displayValue} (${ondValueFrom.code})` }}
										</template>
										<template #mobile-question-1-title>
											{{ props.bstQuestion1MobileLabel }}
										</template>
										<template #mobile-question-2-title>
											{{ props.bstQuestion2MobileLabel }}
										</template>
									</OptionBST>
								</div>
							</template>
							<template v-else>
								<div v-if="props.bstNoteTop" class="bst-banner-card">
									<div class="media">
										<img
											v-if="props.bstAssetType === 'image'"
											class="media-img"
											:src="props.bstAssetPath"
										/>
										<video
											v-if="props.bstAssetType === 'video'"
											ref="videoControls"
											class="media-img"
											loop
											muted
											autoplay
											playsinline
										>
											<source :src="props.bstAssetPath" />
										</video>
									</div>
									<div>
										<div class="content">
											<div
												class="bst-banner rounded-xl bg-primary-blue-extralight flex-grow px-5 py-4 gap-4 relative sm:flex-col"
											>
												<div class="flex gap-3 flex-1">
													<!--
												<div class="basis-6 flex-none top-1 relative">
													<icon-fas-circle-info class="fill-primary-blue-base" />
												</div> -->
													<div v-html-sanitize="props.bstNoteTop"></div>
												</div>
												<div
													v-if="props.bstNoteCTAText"
													class="static sm:pl-8 rtl:right-auto rtl:left-6 rtl:sm:pl-0 rtl:sm:pr-8"
												>
													<AppHyperlink
														v-aa="[
															{
																clickName: props.bstNoteCTAText,
																clickComponentType: 'URL',
																componentName: 'OverlayBonusSideTrip',
																componentID: props.componentIdAA,
																BST: true,
															},
															{
																name: props.bstNoteCTAText,
																type: isUrlMatchCurrentHostname(props.bstNoteCTAURL)
																	? 'other'
																	: 'exit',
															},
														]"
														:href="props.bstNoteCTAURL"
														target="_blank"
														:showExternalLinkIcon="false"
													>
														{{ props.bstNoteCTAText }}
													</AppHyperlink>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="bst-edit-search mt-7 mb-7">
									<EditSearchBST
										ref="editSearchBSTEl"
										:bstEditSearchCTAText="props.bstEditSearchCTAText"
										:ondSelection="props.ondSelection"
										:datePicker="props.datePicker"
										:passengerSelection="props.passengerSelection"
										:fromQuestionMobile="props.fromQuestionMobile"
										:toQuestionMobile="props.toQuestionMobile"
										:datePickerQuestionMobile="props.datePickerQuestionMobile"
										:passengerSelectionQuestion="props.passengerSelectionQuestion"
										:componentIdAA="props.componentIdAA"
										:extraONDFilterForFlightSearch="props.extraONDFilterForFlightSearch"
										:enableGlassDesign="props.enableGlassDesign"
										@save-search-data="handleEditSearchCommitData"
									/>
								</div>
								<div v-html-sanitize="props.bstLabel" class="text-lg font-semibold mb-7"></div>

								<div
									:class="[
										'grid gap-6 md:grid-cols-1',
										isOneWay ? 'grid-cols-1' : 'grid-cols-2',
									]"
								>
									<!-- OPTION 1 -->
									<OptionBST
										:isFocused="focusedOption === 'option-1'"
										:title="`Departure: ${ondValueFrom.displayValue} → ${ondValueTo.displayValue}`"
										:description="`${props.visitAnotherCityText} ${ondValueTo.displayValue}`"
										:enableBSTNewDesign="props.enableBSTNewDesign"
										:enableGlassDesign="props.enableGlassDesign"
										@select="focusedOption = 'option-1'"
									/>

									<!-- OPTION 2 -->
									<OptionBST
										v-if="!isOneWay"
										:isFocused="focusedOption === 'option-2'"
										:title="`Return: ${ondValueTo.displayValue} → ${ondValueFrom.displayValue}`"
										:description="`${props.visitAnotherCityText}  ${ondValueFrom.displayValue}`"
										:enableBSTNewDesign="props.enableBSTNewDesign"
										:enableGlassDesign="props.enableGlassDesign"
										@select="focusedOption = 'option-2'"
									/>

								</div>

								<div v-if="focusedOption" class="mt-8">
									<div class="grid grid-cols-2 gap-6 md:grid-cols-1">
										<!-- Airport -->
										<div>
											<p class="font-semibold mb-2">
												{{ airportQuestionLabel }}
											</p>
											<AirportPicker
												v-model="selectedAirport"
												:options="currentContext?.airportList"
												name="bst-location"
												rules="required"
												class="bst-airport-input"
												:labelText="airportInputLabel"
												:enableGlassDesign="props.enableGlassDesign"
											/>
										</div>
										<!-- Date -->
										<div>
											<p class="font-semibold mb-2">
												{{ dateQuestionLabel }}
											</p>
											<DatePicker
												v-model="selectedDate"
												name="bst-date"
												rules="required"
												:ariaLabel="dateInputLabel"
												:minDate="currentContext?.minDate"
												:maxDate="currentContext?.maxDate"
												:enableGlassDesign="props.enableGlassDesign"
											/>
										</div>
									</div>
								</div>
								<div v-if="reviewTripFlow && focusedOption" class="mt-10">
									<p class="font-semibold mb-4">{{ props.reviewYourTripLabel }}</p>
									<!-- DESKTOP -->
									<div v-if="!isMobileViewport" class="bst-review-timeline desktop">
										<!-- FROM -->
										<div class="node">
											<icon-fas-plane-departure class="node-icon start" />
											<div class="label">{{ reviewTripFlow.from.displayValue }}</div>
											<div class="code">{{ reviewTripFlow.from.code }}</div>
										</div>
										<!-- DEPART -->
										<div class="flight-segment">
											<div class="flight-date">
												{{ dayjs(reviewTripFlow.departDate).format('DD MMM YYYY') }}
											</div>
											<div class="flight-line">
												<div class="dot"></div>
												<div class="dash"></div>
												<icon-fas-plane class="plane" />
												<div class="dash"></div>
												<icon-fas-arrow-right class="arrow" />
											</div>
										</div>
										<!-- BST BEFORE -->
										<template v-if="!reviewTripFlow.isReturn">
											<div class="bst-wrapper">
												<span class="bst-circle">
													<icon-fas-umbrella-beach />
												</span>
												<div class="badge">{{ props.freeJustPayTaxesLabel }}</div>
												<!-- LEFT: bst content -->
												<div class="bst-box-inner">
													<div class="sub">
														{{ reviewTripFlow.bstAirport?.label || props.visitMalaysiaLabel
														}}
														<div class="code">{{ reviewTripFlow.bstAirport?.code }}</div>
													</div>
												</div>
												<!-- RIGHT: date + line -->
												<div class="flight-segment">
													<div class="flight-date">
														{{
															reviewTripFlow.bstDate
																? dayjs(reviewTripFlow.bstDate).format('DD MMM YYYY')
																: props.yourDateOfChoiceLabel
														}}
													</div>
													<div class="flight-line">
														<div class="dot"></div>
														<div class="dash"></div>
														<icon-fas-plane class="plane" />
														<div class="dash"></div>
														<icon-fas-arrow-right class="arrow" />
													</div>
												</div>
											</div>
										</template>
										<!-- DEST -->
										<div class="node">
											<icon-fas-location-dot class="node-icon location" />
											<div class="label">{{ reviewTripFlow.to.displayValue }}</div>
											<div class="code">{{ reviewTripFlow.to.code }}</div>
										</div>

										<!-- RETURN -->
										<div v-if="!isOneWay" class="flight-segment">
											<div class="flight-date">
												{{ dayjs(reviewTripFlow.returnDate).format('DD MMM YYYY') }}
											</div>
											<div class="flight-line">
												<div class="dot"></div>
												<div class="dash"></div>
												<icon-fas-plane class="plane" />
												<div class="dash"></div>
												<icon-fas-arrow-right class="arrow" />
											</div>
										</div>
										<template v-if="reviewTripFlow.isReturn">
											<div class="bst-wrapper">
												<span class="bst-circle">
													<icon-fas-umbrella-beach />
												</span>
												<div class="badge">{{ props.freeJustPayTaxesLabel }}</div>

												<div class="sub">
													{{ reviewTripFlow.bstAirport?.label || props.visitMalaysiaLabel }}
													<span class="code">{{ reviewTripFlow.bstAirport?.code }}</span>
												</div>
												<div class="flight-segment">
													<div class="flight-date">
														{{
															reviewTripFlow.bstDate
																? dayjs(reviewTripFlow.bstDate).format('DD MMM YYYY')
																: props.yourDateOfChoiceLabel
														}}
													</div>
													<div class="flight-line">
														<div class="dot"></div>
														<div class="dash"></div>
														<icon-fas-plane class="plane" />
														<div class="dash"></div>
														<icon-fas-arrow-right class="arrow" />
													</div>
												</div>
											</div>
										</template>
										<!-- FINAL -->
										<div v-if="!isOneWay" class="node">
											<icon-fas-play class="node-icon end" />
											<div class="label">{{ reviewTripFlow.from.displayValue }}</div>
											<div class="code">{{ reviewTripFlow.from.code }}</div>
										</div>
									</div>
									<div v-if="isMobileViewport" class="bst-review-timeline mobile">
										<div v-for="(step, index) in mobileSteps" :key="index" class="step">
											<!-- LEFT TIMELINE COLUMN -->
											<div class="timeline">
												<div class="line"></div>
												<div class="icon">
													<icon-fas-plane-departure v-if="index === 0" class="start-icon" />
													<icon-fas-play
														v-else-if="index === mobileSteps.length - 1"
														class="end-icon"
													/>
													<icon-fas-location-dot
														v-else-if="step.type === 'location'"
														class="circle-icon"
													/>

													<div v-else-if="step.type === 'date'" class="flight-icon-group">
														<icon-fas-plane class="plane-icon" />
														<icon-fas-arrow-down class="arrow-icon" />
													</div>
												<!-- <icon-fas-plane-arrival
													v-else
													class="circle-icon"
												/> -->

												</div>
											</div>
											<div class="content">
												<div v-if="step.type === 'location'" class="label">
													{{ step.label }}
												</div>
												<div v-if="step.type === 'date'" class="date">
													{{ step.label }}
												</div>
												<div v-if="step.type === 'bst'" class="bst-box">
													<div class="badge">{{ props.freeJustPayTaxesLabel }}</div>
													<div class="title">
														<span class="bst-circle">
															<!-- <icon-fas-plane-arrival /> -->
															<icon-fas-umbrella-beach />
														</span>

														{{ step.label || props.visitMalaysiaLabel }}
													</div>
												<!-- <div v-if="step.label" class="sub">
													{{ step.label }}
												</div> -->
												</div>
											</div>
										</div>
									</div>
								</div>

							</template>
							<!-- <div class="" style="height: 700px; flex-basis: 700px;"></div> -->
						</div>
					</div>
					<div class="bst-sticky-bar py-6 md:py-3">
						<div class="max-w-[996px] mx-auto lg:w-full flex justify-end md:justify-center md:flex-col">
							<button
								v-if="props.continueWithoutBSTCTAText"
								v-aa="[
									{
										clickName: props.continueWithoutBSTCTAText,
										clickComponentType: 'Button',
										componentName: 'OverlayBonusSideTrip',
										componentID: props.componentIdAA,
										BST: false,
									},
									{
										name: props.continueWithoutBSTCTAText,
										type: 'exit',
									},
								]"
								v-button-press
								type="button"
								class="btn-generic-b px-4 mr-4 font-semibold md:text-center md:text-sm md:p-5 md:mb-4 md:px-0 md:mr-0 md:order-2"
								@click="handleSubmitValid"
							>{{ props.continueWithoutBSTCTAText }}</button>

							<Tooltip
								:renderInvisibleTrigger="false"
								triangleAlignment="center"
								:isDisabled="isMobileViewport"
								class="bst_tooltip md:p-5 md:w-full md:mb-4"
							>
								<template #default>
									<CTAButton
										v-if="props.continueCTAText"
										v-aa="[
											{
												clickName: props.continueCTAText,
												clickComponentType: 'Button',
												componentName: 'OverlayBonusSideTrip',
												componentID: props.componentIdAA,
												BST: true,
											},
											{
												name: props.continueCTAText,
												type: 'exit',
											},
										]"
										:isSubmit="true"
										:disabled="isBtnSubmitDisabled"
									>
										{{ props.continueCTAText }}
									</CTAButton>
								</template>
								<template #mobile-title>
									<slot></slot>
								</template>
								<template
									v-if="editSearchBSTEl?.isEditMode && props.bstDisabledContinueCtaLabel"
									#tooltip-content
								>
									<div v-html-sanitize="props.bstDisabledContinueCtaLabel"></div>
								</template>
							</Tooltip>

						</div>
					</div>
					<FooterBST class="bst-footer md:hidden"></FooterBST>
				</div>
				<TextField variant="input-hidden" name="selectedOption" :modelValue="focusedOption" />
			</formwrapper>
		</div>
	</Transition>
</Teleport>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.OverlayBonusSideTrip {
	z-index: 10000; // must be more than header
	background-color: var(--neutral-white-base);
}

::v-deep(.bst-airport-input .label-text) {
	top: 11px;
}

::v-deep(.MHCalendar) {
	max-width: 100%;
}


:deep(.AirportPicker .multiselect-main.glass-design) {
	height: 81px;
	background-color: #F8F8F8;
	border-radius: 100px;
	border: 2px #F8F8F8;
    --ms-radius: 100px;
	.btn-clear {
		display: none !important;
	}

	.placeholder-wrapper {

		.text-label {
			font-weight: 700;
			font-size: 10px;
			letter-spacing: 0.62px;
			// text-transform: uppercase;
			color: #99A1AF;
			transform: translate(0, -0.65rem);
		}

		.text-placeholder {
			font-weight: 700;
			font-style: Bold;
			font-size: 16px;
			letter-spacing: -0.31px;
			margin-bottom: 8px;
			// text-transform: uppercase;
			color: #B0B0B0;
			opacity: 1;
		}

		.text-search {
			display: block;
			color: #6A7282;
			font-weight: 400;
			font-size: 12px;
			margin-top: 7px;
		}

	}

	.value-wrapper {
		padding: 24px 24px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 10px;

		.text-value {
			flex-direction: column;
			align-items: flex-start;
			justify-content: space-between;
			margin-top: 7px;
		}

		.value-label {
			padding: 0;
			font-weight: 700;
			font-style: Bold;
			font-size: 16px;
			letter-spacing: -0.31px;
			color: #101828;
		}

		.airport-code-tag {
			width: 36px !important;
			height: 16px !important;
			font-size: 10px;
			display: flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 auto;
			margin-left: 0;
			margin-top: 7px;
			font-weight: 700;
		}
	}

	// &:hover {
	// 	border: 2px solid #CEAF87;
	// 	--ms-bg: none !important;
	// }

	&:focus,
	&:focus-within {
		border: 2px solid var(--secondary-gold-base);
		--ms-bg: none !important;
	}

}

.inner-container {
	width: 100%;
	height: 100vh;
	max-height: inherit;
}

.bst-sticky-bar {
	background-color: var(--neutral-white-base);
	border-top: 2px solid var(--neutral-grey-light);
	position: sticky;
	bottom: 0;
	z-index: 50;
}

.overlay-bottom-enter-from {
	opacity: 0;
}

.overlay-bottom-enter-active,
.overlay-bottom-leave-active {
	transition: opacity 200ms;
}

.overlay-bottom-leave-to {
	opacity: 0;
}

.overlay-bottom-enter-from,
.overlay-bottom-leave-to {
	.overlay-container {
		transform: translateY(75%);
	}
}

.overlay-bottom-enter-active,
.overlay-bottom-leave-active {
	.overlay-container {
		transition: transform 200ms;
	}
}

.overlay-bottom-enter-to,
.overlay-bottom-leave-from {
	.overlay-container {
		transform: translateY(0%);
	}
}

.btn-generic-b {
	border-radius: 48px;
	border: 1.5px solid var(--primary-blue-base, #0D4689);
	background: var(--neutral-white-base, #FFF);
}

::v-deep(.btn-tooltip-trigger) {
  padding: 0px;
  width:100%;

	@media (max-width: 640px) {
		
		.CTAButton {
			width:100%;
		}
	}
}

.bst-banner-card {
	display: flex;
	gap: 16px;
	border-radius: 16px;
	background: #fff;

	@media (max-width: 640px) {
		flex-direction: column;
	}

	.media {
		width: 360px;
		height: 200px;
		border-radius: 14px;
		overflow: hidden;
		background-color: none;

		@media (max-width: 640px) {
			width: 100%;
			height: 180px;
		}
	}

	.media-img,
	.media-video {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.media-fallback {
		background: #fde68a;
		width: 100%;
		height: 100%;
	}

	.content {
		flex: 1;
	}

	.bst-banner {
		background: transparent !important;
		padding: 0 !important;
		border-radius: 0 !important;
	}

	.bst-banner>div:last-child {
		display: flex;
		align-items: center;

		@media (max-width: 640px) {
			padding-left: 0px;
		}
	}
}

.bst-review-timeline {

	&.desktop {
		display: flex;
		align-items: center;
		gap: 18px;
		overflow-x: visible;

		@media (max-width: 1024px) {
			overflow-x: auto;
		}

	}

	.node {
		display: flex;
		flex-direction: column;
		align-items: center;
		min-width: 100px;
	}

	.node-icon.start,
	.node-icon.end {
		background: var(--primary-blue-base);
		font-size: 20px;
		fill: var(--neutral-white-base);
		padding: 6px;
		border-radius: 50%;
		margin-right: 5px;

	}

	.node-icon.location {
		background: var(--primary-blue-base);
		color: var(--primary-blue-base);
		border-radius: 50%;
		padding: 6px;
		font-size: 20px;
		fill: var(--neutral-white-base);
	}

	.label {
		font-size: 13px;
		font-weight: 600;
	}

	.code {
		font-size: 11px;
		color: #6b7280;
	}

	.flight-segment {
		display: flex;
		flex-direction: column;
		align-items: center;
		min-width: 140px;

	}

	.flight-date {
		font-size: 13px;
		margin-bottom: 5px;
		text-align: center;
	}

	.flight-line {
		display: flex;
		align-items: center;
		gap: 6px;
		width: 100%;
	}

	.dot {
		width: 6px;
		height: 6px;
		background: var(--primary-blue-base);
		border-radius: 50%;
	}

	.dash {
		flex: 1;
		border-top: 1.5px dashed var(--primary-blue-base);
	}

	.plane {
		font-size: 12px;
		color: var(--primary-blue-base);
	}

	.arrow {
		font-size: 11px;
		color: var(--primary-blue-base);
	}

	.bst-wrapper {
		position: relative;
		display: flex;
		align-items: center;
		gap: 20px;
		border: 1.5px dashed #00a3ad;
		background: #eef9fb;
		padding: 14px 16px;
		border-radius: 12px;
		min-width: 320px;
	}

	.bst-wrapper .badge {
		position: absolute;
		top: -10px;
		left: 12px;
		background: #00a3ad;
		color: var(--neutral-white-base);
		font-size: 10px;
		font-weight: 700;
		padding: 3px 8px;
		border-radius: 6px;
	}

	.bst-box-inner .title {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		font-weight: 600;

		svg {
			background: var(--primary-blue-base);
			color: var(--primary-blue-base);
			border-radius: 50%;
			padding: 6px;
			font-size: 20px;
			fill: var(--neutral-white-base);
		}
	}

	.bst-wrapper .sub {
		font-size: 12px;
		font-weight: 600;
	}

	.bst-wrapper .bst-circle {
		background: var(--primary-blue-base);
		color: var(--primary-blue-base);
		border-radius: 50%;
		padding: 6px;
		font-size: 15px;
		fill: var(--neutral-white-base);

	}

	&.mobile {
		display: flex;
		flex-direction: column;

		.step {
			display: flex;
			align-items: center;
			gap: 10px;
			position: relative;

			&:not(:last-child) {
				margin-bottom: 6px;
			}

			.timeline {
				position: relative;
				width: 28px;
				min-width: 28px;
				display: flex;
				justify-content: center;

				&::before {
					content: '';
					position: absolute;
					left: 50%;
					top: -6px;
					bottom: -6px;
					width: 2px;
					background: var(--primary-blue-base);
					transform: translateX(-50%);
				}

				.icon {
					width: 28px;
					height: 28px;
					background: #fff;
					display: flex;
					align-items: center;
					justify-content: center;
					z-index: 2;

					.start-icon,
					.end-icon {
						background: var(--primary-blue-base);
						color: var(--neutral-white-base);
						border-radius: 50%;
						padding: 6px;
						font-size: 20px;
						fill: var(--neutral-white-base);
					}

					.circle-icon {
						font-size: 15px;
						background: var(--primary-blue-base);
						color: var(--primary-blue-base);
						border-radius: 50%;
						padding: 6px;
						font-size: 20px;
						fill: var(--neutral-white-base);
					}

					.flight-icon-group {
						display: flex;
						flex-direction: column;
						align-items: center;
						gap: 0;
						line-height: 1;

						.plane-icon {
							font-size: 12px;
							color: var(--primary-blue-base);
						}

						.arrow-icon {
							font-size: 10px;
							color: var(--primary-blue-base);
							margin-top: -4px;
						}
					}
				}
			}

			.content {
				flex: 1;
				padding-bottom: 16px;

				.label {
					font-size: 14px;
					font-weight: 600;
					line-height: 1.4;
				}

				.date {
					font-size: 13px;
					color: #4b5563;
					margin-top: 2px;
				}

				.bst-box {
					position: relative;
					border: 1.5px dashed #00a3ad;
					background: #eef9fb;
					padding: 12px 14px;
					border-radius: 12px;
					margin: 0 0;
					left: -47px;
					z-index: 10;
					top: 12px;

					.badge {
						position: absolute;
						top: -10px;
						left: 12px;
						background: #00a3ad;
						color: #fff;
						font-size: 10px;
						font-weight: 700;
						padding: 3px 8px;
						border-radius: 6px;
					}

					.title {
						display: flex;
						align-items: center;
						gap: 8px;
						font-size: 13px;
						font-weight: 600;


						.bst-circle {
							width: 26px;
							height: 26px;
							background: var(--primary-blue-base);
							color: var(--neutral-white-base);
							border-radius: 50%;
							display: flex;
							align-items: center;
							justify-content: center;
							fill: var(--neutral-white-base);
						}
					}

					.sub {
						font-size: 12px;
						margin-top: 2px;
					}
				}
			}
		}
	}
}
</style>